import {
  ApplicationConfig,
  LOCALE_ID,
  importProvidersFrom,
  provideExperimentalZonelessChangeDetection
} from '@angular/core';
import { provideRouter, withPreloading } from '@angular/router';

import { routes } from './app.routes';
import {
  provideHttpClient,
  withFetch,
  withInterceptors,
  withInterceptorsFromDi
} from '@angular/common/http';
import { provideAnimations } from '@angular/platform-browser/animations';
import { GlobalFacade } from './~store/facades/global.facade';
import { AuthFacade } from './~store/facades/auth.facade';
import { provideHotToastConfig } from '@ngneat/hot-toast';
import {
  popperVariation,
  provideTippyConfig,
  tooltipVariation
} from '@ngneat/helipopper';
import { NgxsModule } from '@ngxs/store';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { GlobalState } from './~store/states/global.state';
import { NgxsActionsExecutingModule } from '@ngxs-labs/actions-executing';
import { NgxsFormPluginModule } from '@ngxs/form-plugin';
import { AuthState, DEFAULT_AUTH_STATE } from './~store/states/auth.state';
import { NgxsStoragePluginModule, STORAGE_ENGINE } from '@ngxs/storage-plugin';
import { LayoutModule } from '@angular/cdk/layout';
import { environment } from '~environments/environment';
import { CustomNgxsStorageEngine } from './~shared/utils/storage';
import { registerLocaleData } from '@angular/common';
import localeFr from '@angular/common/locales/fr';
import { provideClientHydration } from '@angular/platform-browser';
import {
  provideCacheableAnimationLoader,
  provideLottieOptions
} from 'ngx-lottie';
import player from 'lottie-web/build/player/lottie_svg';
import { httpErrorsInterceptor } from './~shared/interceptors/http-errors.interceptors';
import {
  AUTH_STATE_TOKEN,
  GLOBAL_STATE_TOKEN
} from './~shared/constants/global.constants';
import { SsrRequestService } from './~shared/services/ssr-request.service';
import { StorageService } from './~shared/services/storage.service';
import { ProfileService } from './~shared/services/profile.service';
import { ToastService } from './~shared/services/toast.service';
import { CoreModule } from '~core/modules/core.module';
import { StorageModule } from '~core/modules/storage.module';
import { NgxsSelectSnapshotModule } from '@ngxs-labs/select-snapshot';
import { BreakpointsService } from './~shared/services/breakpoints.service';
import { ResourceState } from './~store/states/resource.state';
import { ResourceFacade } from './~store/facades/resource.facade';
import { AuthenticationService } from './~shared/services/authentication.service';
import { LAZYLOAD_IMAGE_HOOKS } from 'ng-lazyload-image';
import { CustomLazyLoadImageHooks } from './~shared/utils/lazy-load';
import { jwtInterceptor } from './~shared/interceptors/jwt.interceptor';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { MatPaginatorI18nService } from './~shared/services/mat-paginator-i18n.service';
import { isEmpty } from 'lodash-es';
import { provideNativeDateAdapter } from '@angular/material/core';
import { UserService } from './~shared/services/user.service';
import { provideAngularSvgIcon } from 'angular-svg-icon';
import { provideNgxMask } from 'ngx-mask';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { UserManagementService } from './~shared/services/user-management.service';
import { NgxPermissionsModule } from 'ngx-permissions';
import { MessageService } from './~shared/services/message.service';
import { BrandService } from './~shared/services/brand.service';
import { CampaignService } from './~shared/services/campaign.service';
import { ScreenService } from './~shared/services/screen.service';
import { TagService } from './~shared/services/tag.service';
import { AdvertiserService } from './~shared/services/advertiser.service';
import { LocationService } from './~shared/services/location.service';
import { ChannelService } from './~shared/services/channel.service';
import { AdsPriceService } from './~shared/services/ads-price.service';
import { FormatService } from './~shared/services/format.service';
import { AdsTypeService } from './~shared/services/ads-type.service';
import { AdvertisingService } from './~shared/services/advertising.service';
import { TrackService } from './~shared/services/track.service';
import { SubscriptionsService } from './~shared/services/subscriptions.service';
import { SubscriptionAdvantagesService } from './~shared/services/subscription-advantages.service';
import { SubscriptionPlansVariationsService } from './~shared/services/subscription-variations.service';
import { SubscriptionPlanService } from './~shared/services/subscription-plan.service';
import { SubscriberService } from './~shared/services/subscriber.service';
import { AccountService } from './~shared/services/account.service';
import { quicklinkProviders, QuicklinkStrategy } from 'ngx-quicklink';
import { DialogService } from './~shared/services/dialog.service';
import { provideLoadingBarInterceptor } from '@ngx-loading-bar/http-client';
import { SubscriberMetricsService } from './~shared/services/subscriber-metrics.service';
import { PaymentState } from './~store/states/payment.state';
import { PaymentFacade } from './~store/facades/payment.facade';
import { PaymentService } from './~shared/services/payment.service';
import { SubscriptionStatusService } from './~shared/services/subscription-status.service';
import { SubscriberMetricsMockService } from './~shared/mocks/subscriber-metrics-mock.service';
import { NgSelectSearchService } from './~shared/services/ng-select-search.service';

registerLocaleData(localeFr, 'fr');

export const appConfig: ApplicationConfig = {
  providers: [
    provideExperimentalZonelessChangeDetection(),
    provideClientHydration(),
    provideRouter(routes, withPreloading(QuicklinkStrategy)),
    provideHttpClient(
      withFetch(),
      withInterceptors([jwtInterceptor, httpErrorsInterceptor]),
      withInterceptorsFromDi()
    ),
    provideLoadingBarInterceptor(),
    quicklinkProviders,
    provideAnimations(),
    { provide: LOCALE_ID, useValue: 'fr' },
    provideHotToastConfig(),
    provideTippyConfig({
      defaultVariation: 'tooltip',
      variations: {
        tooltip: tooltipVariation,
        popper: popperVariation
      }
    }),
    provideLottieOptions({
      player: () => player
    }),
    provideCacheableAnimationLoader(),
    importProvidersFrom([
      NgxsModule.forRoot(
        [GlobalState, AuthState, ResourceState, PaymentState],
        {
          developmentMode: !environment.production
        }
      ),
      NgxsActionsExecutingModule.forRoot(),
      NgxsSelectSnapshotModule.forRoot(),
      NgxsFormPluginModule.forRoot(),
      NgxsStoragePluginModule.forRoot({
        keys: [GlobalState, AuthState],
        beforeSerialize: (obj, key) => {
          if (key === GLOBAL_STATE_TOKEN.getName()) {
            // Save only provided properties on storage
            return {
              routeData: { currentUrl: obj?.routeData?.currentUrl }
            };
          }
          if (key === AUTH_STATE_TOKEN.getName()) {
            return isEmpty(obj)
              ? DEFAULT_AUTH_STATE
              : {
                  currentUser: obj?.currentUser,
                  tokens: obj?.tokens,
                  login: DEFAULT_AUTH_STATE.login,
                  registration: DEFAULT_AUTH_STATE.registration,
                  reset: DEFAULT_AUTH_STATE.reset
                };
          }
          return obj;
        }
      }),
      NgxsReduxDevtoolsPluginModule.forRoot(),
      LayoutModule,
      CoreModule,
      StorageModule,
      NgxPermissionsModule.forRoot()
    ]),
    provideNativeDateAdapter(),
    provideNgxMask(),
    provideAngularSvgIcon(),
    {
      provide: STORAGE_ENGINE,
      useClass: CustomNgxsStorageEngine
    },
    BreakpointsService,
    StorageService,
    SsrRequestService,
    GlobalFacade,
    ProfileService,
    DialogService,
    ToastService,
    {
      provide: LAZYLOAD_IMAGE_HOOKS,
      useClass: CustomLazyLoadImageHooks
    },
    AuthFacade,
    ResourceFacade,
    AuthenticationService,
    UserManagementService,
    NgSelectSearchService,
    UserService,
    MessageService,
    BrandService,
    CampaignService,
    AuthenticationService,
    ScreenService,
    TagService,
    AdvertiserService,
    LocationService,
    ChannelService,
    AdsPriceService,
    FormatService,
    AdsTypeService,
    AdvertisingService,
    TrackService,
    SubscriptionsService,
    SubscriptionAdvantagesService,
    SubscriptionPlansVariationsService,
    SubscriptionPlanService,
    SubscriberService,
    AccountService,
    // SubscriberMetricsService,
    {
      provide: SubscriberMetricsService,
      useClass: SubscriberMetricsMockService
    },
    PaymentFacade,
    PaymentService,
    SubscriptionStatusService,
    { provide: MatPaginatorIntl, useClass: MatPaginatorI18nService },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'outline' }
    }
  ]
};
